import React from 'react';
import { CountryCode } from '@dayetopia/types';
import { IConditionScores, ReportConditionRating } from '@components/dashboard/types/periodPainConsultation';
import Colors from '@components/shared/Colors';
import { BadgeType, ConditionType, StringIndexedNumberObject, ScreeningType } from '@contracts';
import ExclamationPurpleIcon from '@images/vms-account/vms-icons/ExclamationPurple.svg';
import ExclamationYellowIcon from '@images/vms-account/vms-icons/ExclamationYellow.svg';
import PlusIcon from '@images/vms-account/vms-icons/FilledPlus.svg';
import GreenTickIcon from '@images/vms-account/vms-icons/GreenTick.svg';
import StarAltIcon from '@images/vms-account/vms-icons/StarAlt.svg';
import { calculateAge } from '@utils/date';
import { StringIndexedObject } from '../types/common';

interface PrescriptionRecommendationsConfig {
  regular: number;
  super: number;
  boxes: number;
}

export interface PainkillerContext {
  otcPainkiller: string;
  prescriptionPainkiller: string;
  otcPainkillerDose: string;
  otcPainkillerDoseExceeded: string;
  otcPainkillerFrequency: string;
  prescriptionPainkillerDoseExceeded: string;
  adverseAffects: string[];
}

export interface PainConsultationData {
  [key: string]: string | string[] | StringIndexedObject | number;
}

interface PrescriptionRecommendations {
  cbdTampons: {
    eligible: boolean;
    recommended: boolean;
    config: PrescriptionRecommendationsConfig;
  };
}

export type OtherSymptomResourceRecommendations = string[];

export interface GeneratedPPCData {
  timestampDate: Date;
  originalData: PainConsultationData;
  painValue: number;
  conditions: IConditionScores;
  painkillers: PainkillerRiskData;
  painReliefRecommendations: StringIndexedNumberObject;
  otherSymptomResourceRecommendations: OtherSymptomResourceRecommendations;
  prescriptions: PrescriptionRecommendations;
}

interface PainkillerRiskData {
  context: PainkillerContext;
  riskValue: number;
  riskContent: IconTextObject[];
  safetyContent: IconTextObject[];
}

interface PainReliefValue {
  communityEfficacy: number;
  generalSubtotal: number;
}

interface IconTextObject {
  icon: JSX.Element;
  text: string;
  link?: string;
  backgroundColor?: string;
  borderColor?: string;
}

interface ScoreMapData {
  conditions: Array<ConditionType>;
  pain?: Array<string>;
  symptoms?: Array<string>;
  countIncrement?: number;
}

interface IConditionsMap {
  [key: string]: {
    data?: { [key: string]: ScoreMapData };
    groups?: { [key: string]: { members: Array<string>; maxCount: number }[] };
    totalIncrement?: { [condition: string]: number };
  };
}

const formatString = (input: string): string =>
  input
    ?.split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const parseNumberFromString = (input: string): number | null => {
  const match = input.match(/(\d+)/);
  return match ? parseInt(match[0], 10) : null;
};

export const mapConditionRatingToBadgeType = (conditionRating: ReportConditionRating): BadgeType | undefined => {
  switch (conditionRating) {
    case ReportConditionRating.LOW:
      return 'positive';
    case ReportConditionRating.MEDIUM:
      return 'recommended';
    case ReportConditionRating.HIGH:
      return 'negative';
    case ReportConditionRating.DIAGNOSED:
      return 'conclusive';
    default:
      return 'neutral';
  }
};

const questionAnswersMappedToConditions: IConditionsMap = {
  'age-first-period': {
    data: {
      '<8': {
        conditions: [ConditionType.ENDOMETRIOSIS],
        symptoms: ['Getting your first period before the age of 10']
      },
      '8-10': {
        conditions: [ConditionType.ENDOMETRIOSIS],
        symptoms: ['Getting your first period before the age of 10']
      }
    },
    totalIncrement: {
      [ConditionType.ENDOMETRIOSIS]: 1
    }
  },
  'first-experience-menstrual-cramps': {
    data: {
      'shortly-after-first-period': {
        conditions: [ConditionType.PD],
        pain: ['Started with one of your very first periods']
      },
      'a-few-years-after-first-period': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PCOS, ConditionType.ADENOMYOSIS, ConditionType.PID],
        pain: ['Started years after your first period']
      },
      'more-than-10-years-after-first-period': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PCOS, ConditionType.ADENOMYOSIS, ConditionType.PID, ConditionType.FIBROIDS],
        pain: ['Started years after your first period']
      }
    },
    totalIncrement: {
      [ConditionType.PD]: 1,
      [ConditionType.FIBROIDS]: 1,
      [ConditionType.ENDOMETRIOSIS]: 1,
      [ConditionType.PCOS]: 1,
      [ConditionType.ADENOMYOSIS]: 1,
      [ConditionType.PID]: 1
    }
  },
  'when-does-pain-occur': {
    data: {
      'before-period': {
        conditions: [ConditionType.PD, ConditionType.ENDOMETRIOSIS, ConditionType.ADENOMYOSIS, ConditionType.FIBROIDS],
        pain: ['Occurs just before AND/OR during your period']
      },
      'during-period': {
        conditions: [
          ConditionType.PD,
          ConditionType.ENDOMETRIOSIS,
          ConditionType.PCOS,
          ConditionType.ADENOMYOSIS,
          ConditionType.PID,
          ConditionType.FIBROIDS
        ],
        pain: ['Occurs just before AND/OR during your period']
      },
      'after-period': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.ADENOMYOSIS, ConditionType.PID, ConditionType.FIBROIDS],
        pain: ['Occurs after your period']
      },
      'when-i-ovulate': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.ADENOMYOSIS, ConditionType.PID, ConditionType.FIBROIDS]
      },
      throughout: {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.ADENOMYOSIS, ConditionType.PID, ConditionType.FIBROIDS],
        pain: ['Is unpredictable and occurs throughout your cycle']
      }
    },
    groups: {
      [ConditionType.PD]: [
        {
          members: ['before-period', 'during-period'],
          maxCount: 1
        }
      ],
      [ConditionType.ENDOMETRIOSIS]: [
        {
          members: ['before-period', 'during-period'],
          maxCount: 1
        },
        {
          members: ['when-i-ovulate'],
          maxCount: 1
        },
        {
          members: ['throughout'],
          maxCount: 1
        }
      ],
      [ConditionType.ADENOMYOSIS]: [
        {
          members: ['before-period', 'during-period'],
          maxCount: 1
        },
        {
          members: ['when-i-ovulate'],
          maxCount: 1
        },
        {
          members: ['throughout'],
          maxCount: 1
        }
      ],
      [ConditionType.FIBROIDS]: [
        {
          members: ['before-period', 'during-period'],
          maxCount: 1
        },
        {
          members: ['when-i-ovulate'],
          maxCount: 1
        },
        {
          members: ['throughout'],
          maxCount: 1
        }
      ]
    },
    totalIncrement: {
      [ConditionType.PD]: 1,
      [ConditionType.ENDOMETRIOSIS]: 3,
      [ConditionType.ADENOMYOSIS]: 3,
      [ConditionType.FIBROIDS]: 3,
      [ConditionType.PCOS]: 1,
      [ConditionType.PID]: 3
    }
  },
  'how-severe-pain': {
    data: {
      '5': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PCOS, ConditionType.ADENOMYOSIS, ConditionType.PID, ConditionType.FIBROIDS],
        pain: ['Is very intense']
      },
      '6': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PCOS, ConditionType.ADENOMYOSIS, ConditionType.PID, ConditionType.FIBROIDS],
        pain: ['Is very intense']
      },
      '7': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PCOS, ConditionType.ADENOMYOSIS, ConditionType.PID, ConditionType.FIBROIDS],
        pain: ['Is very intense']
      },
      '8': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PCOS, ConditionType.ADENOMYOSIS, ConditionType.PID, ConditionType.FIBROIDS],
        pain: ['Is very intense']
      },
      '9': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PCOS, ConditionType.ADENOMYOSIS, ConditionType.PID, ConditionType.FIBROIDS],
        pain: ['Is very intense']
      },
      '10': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PCOS, ConditionType.ADENOMYOSIS, ConditionType.PID, ConditionType.FIBROIDS],
        pain: ['Is very intense']
      }
    },
    totalIncrement: {
      [ConditionType.ENDOMETRIOSIS]: 1,
      [ConditionType.PCOS]: 1,
      [ConditionType.ADENOMYOSIS]: 1,
      [ConditionType.PID]: 1,
      [ConditionType.FIBROIDS]: 1
    }
  },
  'how-long-does-pain-last': {
    data: {
      '<1-day': {
        conditions: [ConditionType.PD],
        pain: ['Lasts for less than 3 days']
      },
      'between-1-3-days': {
        conditions: [ConditionType.PD],
        pain: ['Lasts for less than 3 days']
      },
      '>3': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PCOS, ConditionType.ADENOMYOSIS, ConditionType.PID, ConditionType.FIBROIDS],
        pain: ['Lasts for more than 3 days or duration is unpredictable']
      },
      varies: {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PCOS, ConditionType.ADENOMYOSIS, ConditionType.PID, ConditionType.FIBROIDS],
        pain: ['Lasts for more than 3 days or duration is unpredictable']
      }
    },
    totalIncrement: {
      [ConditionType.PD]: 1,
      [ConditionType.ENDOMETRIOSIS]: 1,
      [ConditionType.PCOS]: 1,
      [ConditionType.ADENOMYOSIS]: 1,
      [ConditionType.PID]: 1,
      [ConditionType.FIBROIDS]: 1
    }
  },
  'describe-pain-pattern': {
    data: {
      'intermittent-pain': {
        conditions: [ConditionType.PD],
        pain: ['Comes and goes in waves']
      }
    },
    totalIncrement: {
      [ConditionType.PD]: 1
    }
  },
  'describe-pain-sensation': {
    data: {
      'pressure-in-pelvis': {
        conditions: [ConditionType.FIBROIDS],
        pain: ['Feels like pressure in the pelvis']
      }
    },
    totalIncrement: {
      [ConditionType.FIBROIDS]: 1
    }
  },
  'pain-radiate-other-areas-body': {
    data: {
      'lower-back': {
        conditions: [ConditionType.PD, ConditionType.ENDOMETRIOSIS, ConditionType.ADENOMYOSIS],
        pain: ['Radiates to your lower back']
      },
      thighs: {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PCOS, ConditionType.ADENOMYOSIS, ConditionType.FIBROIDS],
        pain: ['Radiates to other parts of your body (legs, rectum or groin)']
      },
      hips: {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.ADENOMYOSIS],
        pain: ['Radiates to other parts of your body (legs, rectum or groin)']
      },
      groin: {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.ADENOMYOSIS],
        pain: ['Radiates to other parts of your body (legs, rectum or groin)']
      },
      rectum: {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.ADENOMYOSIS, ConditionType.PID],
        pain: ['Radiates to other parts of your body (legs, rectum or groin)']
      },
      shoulder: {
        conditions: [ConditionType.ENDOMETRIOSIS],
        pain: ['Radiates to your shoulder']
      },
      none: {
        conditions: [ConditionType.PD],
        pain: ['Is only felt in your abdomen']
      }
    },
    groups: {
      [ConditionType.PD]: [
        {
          members: ['lower-back'],
          maxCount: 1
        },
        {
          members: ['none'],
          maxCount: 1
        }
      ],
      [ConditionType.ENDOMETRIOSIS]: [
        {
          members: ['lower-back'],
          maxCount: 1
        },
        {
          members: ['thighs', 'hips', 'groin', 'rectum'],
          maxCount: 1
        },
        {
          members: ['shoulder'],
          maxCount: 1
        }
      ],
      [ConditionType.PCOS]: [
        {
          members: ['thighs'],
          maxCount: 1
        }
      ],
      [ConditionType.ADENOMYOSIS]: [
        {
          members: ['lower-back'],
          maxCount: 1
        },
        {
          members: ['thighs', 'hips', 'groin', 'rectum'],
          maxCount: 1
        }
      ],
      [ConditionType.PID]: [
        {
          members: ['rectum'],
          maxCount: 1
        }
      ],
      [ConditionType.FIBROIDS]: [
        {
          members: ['thighs'],
          maxCount: 1
        }
      ]
    },
    totalIncrement: {
      [ConditionType.PD]: 2,
      [ConditionType.ENDOMETRIOSIS]: 3,
      [ConditionType.PCOS]: 1,
      [ConditionType.ADENOMYOSIS]: 2,
      [ConditionType.FIBROIDS]: 1,
      [ConditionType.PID]: 1
    }
  },
  'period-predictability': {
    data: {
      'very-regular': {
        conditions: [ConditionType.PD],
        symptoms: ['Regular periods']
      },
      'usually-regular': {
        conditions: [ConditionType.PD],
        symptoms: ['Regular periods']
      },
      irregular: {
        conditions: [ConditionType.PCOS, ConditionType.ADENOMYOSIS, ConditionType.FIBROIDS],
        symptoms: ['Irregular periods']
      },
      'very-rare': {
        conditions: [ConditionType.PCOS],
        symptoms: ['Rare or no periods']
      },
      'no-periods': {
        conditions: [ConditionType.PCOS],
        symptoms: ['Rare or no periods']
      }
    },
    totalIncrement: {
      [ConditionType.PD]: 1,
      [ConditionType.PCOS]: 3,
      [ConditionType.ADENOMYOSIS]: 1,
      [ConditionType.FIBROIDS]: 1
    }
  },
  'period-scale': {
    data: {
      'very-heavy': {
        conditions: [ConditionType.ADENOMYOSIS, ConditionType.ENDOMETRIOSIS, ConditionType.FIBROIDS],
        symptoms: ['Very heavy periods']
      }
    },
    totalIncrement: {
      [ConditionType.ENDOMETRIOSIS]: 1,
      [ConditionType.ADENOMYOSIS]: 1,
      [ConditionType.FIBROIDS]: 1
    }
  },
  'average-cycle-length': {
    data: {
      '<21': {
        conditions: [ConditionType.PCOS],
        symptoms: ['If menstruating, cycle is shorter or longer than usual (less than 21 days or more than 32 days)']
      },
      '21-25': {
        conditions: [ConditionType.PD],
        symptoms: ['Cycle length is between 21 and 31 days']
      },
      '26-31': {
        conditions: [ConditionType.PD],
        symptoms: ['Cycle length is between 21 and 31 days']
      },
      '32+': {
        conditions: [ConditionType.PCOS],
        symptoms: ['If menstruating, cycle is shorter or longer than usual (less than 21 days or more than 32 days)']
      }
    },
    totalIncrement: {
      [ConditionType.PD]: 1,
      [ConditionType.PCOS]: 2
    }
  },
  'bleed-length': {
    data: {
      '3-5': {
        conditions: [ConditionType.PD],
        symptoms: ['Periods last between 3 and 8 days']
      },
      '6-8': {
        conditions: [ConditionType.PD],
        symptoms: ['Periods last between 3 and 8 days']
      },
      '9+': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.ADENOMYOSIS, ConditionType.FIBROIDS],
        symptoms: ['Menstruation lasts more than 8 days']
      }
    },
    totalIncrement: {
      [ConditionType.PD]: 1,
      [ConditionType.ENDOMETRIOSIS]: 1,
      [ConditionType.ADENOMYOSIS]: 1,
      [ConditionType.FIBROIDS]: 1
    }
  },
  'experience-other-symptoms': {
    data: {
      clotting: {
        conditions: [ConditionType.ADENOMYOSIS],
        symptoms: ['Clotting']
      },
      bloating: {
        conditions: [ConditionType.PD, ConditionType.ENDOMETRIOSIS, ConditionType.ADENOMYOSIS, ConditionType.FIBROIDS],
        symptoms: ['Bloating']
      },
      'nausea-vomiting': {
        conditions: [ConditionType.PD, ConditionType.PID],
        symptoms: ['Feeling sick (nausea or vomitting)']
      },
      'headaches-migraines': {
        conditions: [ConditionType.PD],
        symptoms: ['Headaches or migraines']
      }
    },
    totalIncrement: {
      [ConditionType.ADENOMYOSIS]: 2,
      [ConditionType.PD]: 3,
      [ConditionType.ENDOMETRIOSIS]: 1,
      [ConditionType.FIBROIDS]: 1,
      [ConditionType.PID]: 1
    }
  },
  'experience-symptoms-cycle': {
    data: {
      'pee-frequently': {
        conditions: [ConditionType.FIBROIDS],
        symptoms: ['Need to pee frequently']
      },
      'spotting-outside-period': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PID, ConditionType.FIBROIDS],
        symptoms: ['Spotting or bleeding outside my period']
      },
      'burning-sensation-vagina': {
        conditions: [ConditionType.PID],
        symptoms: ['Burning sensation in the vagina']
      },
      'pain-while-peeing': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PID],
        symptoms: ['Pain/burning while peeing']
      },
      'pain-during-bowel-movements': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.ADENOMYOSIS],
        symptoms: ['Pain/burning during bowel movements']
      },
      'pain-during-sex': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.ADENOMYOSIS, ConditionType.PID],
        symptoms: ['Pain during penetrative sex']
      },
      'bleeding-after-sex': {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PID],
        symptoms: ['Bleeding after sex']
      },
      'vulval-discomfort': {
        conditions: [ConditionType.PID],
        symptoms: ['Vulval discomfort (itchiness, pain, redness, or swelling)']
      },
      'vaginal-discomfort': {
        conditions: [ConditionType.PID],
        symptoms: ['Vaginal discomfort (itchiness, pain, or swelling)']
      },
      'unusual-vaginal-discharge': {
        conditions: [ConditionType.PID],
        symptoms: ['Unusual vaginal discharge (heavy or with unusual odour)']
      },
      acne: {
        conditions: [ConditionType.PCOS],
        symptoms: ['Severce acne']
      },
      'hair-loss': {
        conditions: [ConditionType.PCOS],
        symptoms: ['Hair loss']
      },
      'excessive-facial-or-body-hair': {
        conditions: [],
        symptoms: ['Excessive facial or body hair']
      },
      constipation: {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.FIBROIDS],
        symptoms: ['Constipation']
      },
      fever: {
        conditions: [ConditionType.PID],
        symptoms: ['Fever (greater than 38°C)']
      },
      'weight-issues': {
        conditions: [ConditionType.PCOS],
        symptoms: ['Weight gain or difficulty losing weight']
      },
      fatigue: {
        conditions: [ConditionType.ENDOMETRIOSIS],
        symptoms: ['Fatigue']
      },
      diarrhoea: {
        conditions: [ConditionType.PD, ConditionType.PID],
        symptoms: ['Diarrhoea']
      },
      none: {
        conditions: [ConditionType.PD],
        symptoms: ['No symptoms of pelvic pain conditions']
      }
    },
    totalIncrement: {
      [ConditionType.FIBROIDS]: 3,
      [ConditionType.ENDOMETRIOSIS]: 7,
      [ConditionType.PID]: 10,
      [ConditionType.PCOS]: 3,
      [ConditionType.ADENOMYOSIS]: 2,
      [ConditionType.PD]: 2
    }
  },
  // 'hair-growth-scale': {},
  'medical-professional-conditions': {
    data: {
      [ConditionType.ADENOMYOSIS]: {
        conditions: [ConditionType.ADENOMYOSIS]
      },
      [ConditionType.ENDOMETRIOSIS]: {
        conditions: [ConditionType.ENDOMETRIOSIS]
      },
      [ConditionType.FIBROIDS]: {
        conditions: [ConditionType.FIBROIDS]
      },
      pid: {
        conditions: [ConditionType.PID]
      },
      [ConditionType.PCOS]: {
        conditions: [ConditionType.PCOS]
      },
      'polycystic-ovaries': {
        conditions: [ConditionType.PCOS]
      },
      none: {
        conditions: [ConditionType.PD],
        symptoms: ['No previous diagnosis of pelvic pain conditions'],
        countIncrement: 5
      }
    },
    totalIncrement: {
      [ConditionType.ADENOMYOSIS]: 1,
      [ConditionType.ENDOMETRIOSIS]: 1,
      [ConditionType.FIBROIDS]: 1,
      [ConditionType.PID]: 1,
      [ConditionType.PCOS]: 2,
      [ConditionType.PD]: 5
    }
  },
  'infections-within-last-year': {
    data: {
      chlamydia: {
        conditions: [ConditionType.PID],
        symptoms: ['Recent STI infection (Chlamydia, Gonorrhea, or Mycoplasma genitalium)']
      },
      gonorrhea: {
        conditions: [ConditionType.PID],
        symptoms: ['Recent STI infection (Chlamydia, Gonorrhea, or Mycoplasma genitalium)']
      },
      mycoplasma: {
        conditions: [ConditionType.PID],
        symptoms: ['Recent STI infection (Chlamydia, Gonorrhea, or Mycoplasma genitalium)']
      }
    },
    totalIncrement: {
      [ConditionType.PID]: 1
    }
  },
  'other-conditions': {
    data: {
      'insulin-sensitivity': {
        conditions: [ConditionType.PCOS],
        symptoms: ['History of insulin resistance or diabetes']
      },
      'type-1-diabetes': {
        conditions: [ConditionType.PCOS],
        symptoms: ['History of insulin resistance or diabetes']
      },
      'type-2-diabetes': {
        conditions: [ConditionType.PCOS],
        symptoms: ['History of insulin resistance or diabetes']
      }
    },
    totalIncrement: {
      [ConditionType.PCOS]: 1
    }
  },
  'gynaecological-conditions': {
    data: {
      [ConditionType.ADENOMYOSIS]: {
        conditions: [ConditionType.ADENOMYOSIS],
        symptoms: ['Family history of adenomyosis']
      },
      [ConditionType.ENDOMETRIOSIS]: {
        conditions: [ConditionType.ENDOMETRIOSIS],
        symptoms: ['Family history of endometriosis']
      },
      [ConditionType.FIBROIDS]: {
        conditions: [ConditionType.FIBROIDS],
        symptoms: ['Family history of fibroids']
      },
      [ConditionType.PCOS]: {
        conditions: [ConditionType.PCOS],
        symptoms: ['Family history of PCOS']
      }
    },
    totalIncrement: {
      [ConditionType.ADENOMYOSIS]: 1,
      [ConditionType.ENDOMETRIOSIS]: 1,
      [ConditionType.FIBROIDS]: 1,
      [ConditionType.PCOS]: 1
    }
  },
  'unable-to-conceive-1-year': {
    data: {
      yes: {
        conditions: [ConditionType.ENDOMETRIOSIS, ConditionType.PCOS, ConditionType.PID],
        symptoms: ['Difficulty getting pregnant']
      }
    },
    totalIncrement: {
      [ConditionType.ENDOMETRIOSIS]: 1,
      [ConditionType.PCOS]: 1,
      [ConditionType.PID]: 1
    }
  },
  'previously-pregnant': {
    data: {
      no: {
        conditions: [ConditionType.FIBROIDS],
        symptoms: ['No previous pregnancies']
      }
    },
    totalIncrement: {
      [ConditionType.FIBROIDS]: 1
    }
  },
  'pregnancy-history': {
    data: {
      miscarriage: {
        conditions: [ConditionType.PCOS, ConditionType.ADENOMYOSIS],
        symptoms: ['History of miscarriage']
      },
      'ectopic-pregnancy': {
        conditions: [ConditionType.PID],
        symptoms: ['History of ectopic pregnancy']
      },
      'preterm-labour': {
        conditions: [ConditionType.ADENOMYOSIS, ConditionType.PID, ConditionType.FIBROIDS],
        symptoms: ['History of preterm labour']
      }
    },
    totalIncrement: {
      [ConditionType.PCOS]: 1,
      [ConditionType.ADENOMYOSIS]: 2,
      [ConditionType.PID]: 2,
      [ConditionType.FIBROIDS]: 1
    }
  }
};

interface ScreeningResultParameter {
  value: number;
  parameterName?: string;
  isVmsSpecific?: boolean;
  isStiSpecific?: boolean;
  isHpvSpecific?: boolean;
}

function getScreeningTypeConditionFunction(screeningType?: ScreeningType): (r?: ScreeningResultParameter) => boolean {
  if (screeningType) {
    switch (screeningType) {
      case ScreeningType.Vms: {
        return (r?: ScreeningResultParameter) => !!r?.isVmsSpecific;
      }
      case ScreeningType.Sti: {
        return (r?: ScreeningResultParameter) => !!r?.isStiSpecific;
      }
      case ScreeningType.Hpv: {
        return (r?: ScreeningResultParameter) => !!r?.isHpvSpecific;
      }
      default:
        return () => true;
    }
  }
  return () => true;
}

function appendToScoreArray(
  scores: IConditionScores,
  key: keyof Pick<IConditionScores, 'your-pain' | 'symptoms-and-signs'>,
  items: string[],
  screeningType?: ScreeningType
): void {
  const screeningTypeConditionFunction = getScreeningTypeConditionFunction(screeningType);
  const filteredItems = items.filter((item) => screeningTypeConditionFunction({ value: 0, parameterName: item }));
  scores[key].push(...filteredItems);
}

export const calculateConditionScores = (userQuestionAnswersData: PainConsultationData, screeningType?: ScreeningType): IConditionScores => {
  const conditionScores: IConditionScores = {
    painByCondition: 0,
    scores: {
      [ConditionType.PD]: { count: 0, total: 0, percentage: 0, riskScore: ReportConditionRating.LOW },
      [ConditionType.ENDOMETRIOSIS]: { count: 0, total: 0, percentage: 0, riskScore: ReportConditionRating.LOW },
      [ConditionType.PCOS]: { count: 0, total: 0, percentage: 0, riskScore: ReportConditionRating.LOW },
      [ConditionType.ADENOMYOSIS]: { count: 0, total: 0, percentage: 0, riskScore: ReportConditionRating.LOW },
      [ConditionType.PID]: { count: 0, total: 0, percentage: 0, riskScore: ReportConditionRating.LOW },
      [ConditionType.FIBROIDS]: { count: 0, total: 0, percentage: 0, riskScore: ReportConditionRating.LOW }
    },
    'your-pain': [],
    'symptoms-and-signs': []
  };

  const questionMap = (question: string) => questionAnswersMappedToConditions[question];

  // Calculate total
  Object.keys(questionAnswersMappedToConditions).forEach((key) => {
    const questionData = questionMap(key);
    const answersMappedToConditions = questionData?.data;

    if (answersMappedToConditions) {
      const conditionsFound = new Set<ConditionType>();
      Object.values(answersMappedToConditions).forEach((values) => {
        values.conditions.forEach((conditionName: ConditionType) => {
          conditionsFound.add(conditionName);
        });
      });

      conditionsFound.forEach((conditionName: ConditionType) => {
        const increment = questionData?.totalIncrement?.[conditionName] || 1;
        conditionScores.scores[conditionName].total += increment;
      });
    }
  });

  const bodyHairOvergrowth = userQuestionAnswersData['hair-growth-scale'];

  if (bodyHairOvergrowth) {
    const pcosHairOvergrowthMinimum = 4;
    const hairOvergrowthSum = Object.values(bodyHairOvergrowth).reduce((total, num) => total + Number(num), 0);
    if (hairOvergrowthSum > pcosHairOvergrowthMinimum) {
      conditionScores.scores[ConditionType.PCOS].count += 1;
    }
  }
  conditionScores.scores[ConditionType.PCOS].total += 1; // increment for hair growth scale logic

  // Iterate over each question and calculate count and add to your-pain and symptoms-and-signs
  Object.keys(userQuestionAnswersData).forEach((questionName) => {
    const userAnswersData = userQuestionAnswersData[questionName];

    const questionData = questionMap(questionName);
    const answersMappedToConditions = questionData?.data;
    const questionConditionsGroups = questionData?.groups;

    // Initialize groupCounts for each condition
    // TODO: This doesnt seem to be used anywhere
    const groupCounts: Record<ConditionType, Record<number, number>> = {
      [ConditionType.ENDOMETRIOSIS]: {},
      [ConditionType.PID]: {},
      [ConditionType.ADENOMYOSIS]: {},
      [ConditionType.PCOS]: {},
      [ConditionType.FIBROIDS]: {},
      [ConditionType.PD]: {}
    };

    if (questionData && answersMappedToConditions) {
      const isAnswersArray = Array.isArray(userAnswersData);
      const isAnswersString = typeof userAnswersData === 'string';
      const isAnswersObject = typeof userAnswersData === 'object';

      if (isAnswersArray) {
        userAnswersData.forEach((answerName: string) => {
          const conditionsAndSymptoms = answersMappedToConditions[answerName];
          if (conditionsAndSymptoms) {
            const { conditions, pain, symptoms, countIncrement = 1 } = conditionsAndSymptoms;

            conditions.forEach((conditionName: ConditionType) => {
              const questionConditionGroup = questionData?.groups?.[conditionName];
              // TODO: This doesnt seem to be used anywhere:
              // 1. The groupCounts doesnt seem to be used anywhere
              // 2. conditionScores.scores[conditionName].count doesnt seem to be used anywhere
              if (questionConditionsGroups && questionConditionGroup) {
                Object.keys(questionConditionGroup).forEach((groupIndex) => {
                  const index = Number(groupIndex);
                  const group = questionConditionGroup[index];

                  if (group.members.includes(answerName)) {
                    if (!groupCounts[conditionName][index]) {
                      groupCounts[conditionName][index] = 0;
                    }

                    if (groupCounts[conditionName][index] < group.maxCount) {
                      conditionScores.scores[conditionName].count += countIncrement;
                      groupCounts[conditionName][index] += 1;
                    }
                  }
                });
              } else {
                conditionScores.scores[conditionName].count += countIncrement;
              }
            });

            if (pain) appendToScoreArray(conditionScores, 'your-pain', pain, screeningType);
            if (symptoms) appendToScoreArray(conditionScores, 'symptoms-and-signs', symptoms, screeningType);
          }
        });
      } else if (isAnswersString && answersMappedToConditions[userAnswersData]) {
        const conditionsAndSymptoms = answersMappedToConditions[userAnswersData];
        const { conditions, pain, symptoms, countIncrement = 1 } = conditionsAndSymptoms;

        conditions?.forEach((conditionName: ConditionType) => {
          conditionScores.scores[conditionName].count += countIncrement;
        });

        if (pain) appendToScoreArray(conditionScores, 'your-pain', pain, screeningType);
        if (symptoms) appendToScoreArray(conditionScores, 'symptoms-and-signs', symptoms, screeningType);
      } else if (isAnswersObject) {
        const seenConditions = new Set<ConditionType>();
        const seenPainPoints = new Set<string>();
        const seenSymptomPoints = new Set<string>();

        Object.keys(userAnswersData).forEach((answerName) => {
          const conditionsAndSymptoms = answersMappedToConditions[answerName];
          if (conditionsAndSymptoms) {
            const { conditions, pain, symptoms } = answersMappedToConditions[answerName];

            conditions.forEach((conditionName: ConditionType) => {
              const questionConditionGroup = questionData?.groups?.[conditionName];
              // TODO: This doesnt seem to be used anywhere:
              // 1. The groupCounts doesnt seem to be used anywhere
              // 2. conditionScores.scores[conditionName].count doesnt seem to be used anywhere
              if (questionConditionsGroups && questionConditionGroup) {
                Object.keys(questionConditionGroup).forEach((groupIndex) => {
                  const index = Number(groupIndex);
                  const group = questionConditionGroup[index];

                  if (group.members.includes(answerName)) {
                    if (!groupCounts[conditionName][index]) {
                      groupCounts[conditionName][index] = 0;
                    }

                    if (groupCounts[conditionName][index] < group.maxCount) {
                      conditionScores.scores[conditionName].count += 1;
                      groupCounts[conditionName][index] += 1;
                    }
                  }
                });
              } else {
                conditionScores.scores[conditionName].count += 1;
              }

              seenConditions.add(conditionName);
            });

            pain?.forEach((painPoint) => seenPainPoints.add(painPoint));
            symptoms?.forEach((symptom) => seenSymptomPoints.add(symptom));
          }
        });

        seenConditions.forEach((conditionName: ConditionType) => {
          const countIncrement = answersMappedToConditions[conditionName]?.countIncrement || 1;
          conditionScores.scores[conditionName].count += countIncrement;
        });
        appendToScoreArray(conditionScores, 'your-pain', Array.from(seenPainPoints), screeningType);
        appendToScoreArray(conditionScores, 'symptoms-and-signs', Array.from(seenSymptomPoints), screeningType);
      }
    }
  });

  // After all count and total calculations, calculate percentage and riskScore
  (Object.keys(conditionScores.scores) as ConditionType[]).forEach((key) => {
    const score = conditionScores.scores[key];
    if (score.total > 0) {
      score.percentage = checkIsDiagnosed(userQuestionAnswersData, key) ? 100 : Math.round((score.count / score.total) * 100);

      if (score.percentage === 100) {
        score.riskScore = ReportConditionRating.DIAGNOSED;
      } else if (score.percentage >= 60) {
        score.riskScore = ReportConditionRating.HIGH;
      } else if (score.percentage > 25) {
        score.riskScore = ReportConditionRating.MEDIUM;
      } else {
        score.riskScore = ReportConditionRating.LOW;
      }
    }
  });

  conditionScores.painByCondition = calculatePainByCondition(conditionScores.scores);

  return conditionScores;
};

const checkIsDiagnosed = (data: PainConsultationData, key: string): boolean => {
  if (key === ConditionType.PID) return false;
  const medicalConditions = data['medical-professional-conditions'] as string[];
  if (medicalConditions.includes(key)) return true;
  return false;
};

const calculatePainByCondition = (scores: { [key: string]: { count: number; total: number; percentage: number; riskScore: string } }): number => {
  if (scores.PD && scores.PD.count === scores.PD.total) return 100; // If PD is 100%

  const { PD, ...filteredScores } = scores;
  const riskScores = Object.values(filteredScores).map((score) => score.riskScore);

  if (riskScores.includes('diagnosed')) return 100; // If any condition is diagnosed

  if (riskScores.includes('high')) return 75; // If one or more conditions are high

  if (riskScores.includes('medium')) return 50; // If no conditions are high, but one or more conditions are medium

  return riskScores.every((score) => score === 'low') ? 25 : 0; // If all conditions are low, return 25. Otherwise, return 0
};

export const generatePeriodPainConsultationData = (data: PainConsultationData, country: CountryCode): GeneratedPPCData => {
  const painValue = calculatePainValue(data);
  const prescriptions = generatePrescriptionRecommendations(data, country);
  const conditions = calculateConditionScores(data);
  const painkillers = generatePainkillerRiskData(data);
  const painReliefRecommendations = generatePainReliefRecommendations(data, country, prescriptions);
  const otherSymptomResourceRecommendations = generateOtherSymptomResourceRecommendations(data);

  return {
    originalData: data,
    painValue,
    conditions,
    painkillers,
    painReliefRecommendations,
    otherSymptomResourceRecommendations,
    prescriptions,
    timestampDate: new Date()
  };
};

const generatePrescriptionRecommendations = (data: PainConsultationData, country: CountryCode): PrescriptionRecommendations => {
  const cbdTampons = {
    recommend: false,
    eligible: true
  };

  if (data['when-does-pain-occur'] === 'during-period' || data['when-does-pain-occur'] === 'throughout') {
    cbdTampons.recommend = true;
  }

  if (data['period-predictability'] === 'no-periods-contraception' || data['period-predictability'] === 'no-periods') {
    cbdTampons.eligible = false;
  }
  if ((data['other-conditions'] as string[]).includes('tss')) {
    cbdTampons.eligible = false;
  }
  if (data['currently-trying-to-conceive'] === 'yes') {
    cbdTampons.eligible = false;
  } else {
    cbdTampons.recommend = true;
  }
  if ((data['forms-of-pain-relief-tried'] as StringIndexedObject)['cbd-tampons'] === '0') {
    cbdTampons.eligible = false;
  }
  const allergicReactions = data['allergic-reaction'] as string[];
  if (allergicReactions.includes('cbd') || allergicReactions.includes('cacao-butter')) {
    cbdTampons.eligible = false;
  }
  const dateOfBirth = data['date-of-birth'] as string;
  if (calculateAge(dateOfBirth) >= 18) {
    cbdTampons.recommend = true;
  } else {
    cbdTampons.eligible = false;
  }

  if (country === CountryCode.US) {
    cbdTampons.eligible = false;
    cbdTampons.recommend = false;
  }

  const daysPeriodPainLasts = data['how-long-does-pain-last'] as string;
  const periodScale = data['period-scale'] as string;
  let regularTampons = 0;
  let superTampons = 0;
  let boxes = 1;

  if (daysPeriodPainLasts === '<1-day') {
    boxes = 1;
    if (periodScale === 'heavy' || periodScale === 'very-heavy') {
      regularTampons = 0;
      superTampons = 4;
    } else if (periodScale === 'medium') {
      regularTampons = 2;
      superTampons = 2;
    } else {
      regularTampons = 4;
      superTampons = 0;
    }
  } else if (daysPeriodPainLasts === '>3') {
    boxes = 3;
    if (periodScale === 'heavy' || periodScale === 'very-heavy') {
      regularTampons = 0;
      superTampons = 12;
    } else if (periodScale === 'medium') {
      regularTampons = 6;
      superTampons = 6;
    } else {
      regularTampons = 12;
      superTampons = 0;
    }
  } else {
    boxes = 2;
    if (periodScale === 'heavy' || periodScale === 'very-heavy') {
      regularTampons = 0;
      superTampons = 8;
    } else if (periodScale === 'medium') {
      regularTampons = 4;
      superTampons = 4;
    } else {
      regularTampons = 8;
      superTampons = 0;
    }
  }

  return {
    cbdTampons: {
      eligible: cbdTampons.eligible,
      recommended: cbdTampons.recommend,
      config: {
        regular: regularTampons,
        super: superTampons,
        boxes
      }
    }
  };
};

export const averageProduct = (reportedEfficacy: number, communityEfficacy: number, generalSubtotal: number): number => {
  const efficacy = Number.isNaN(reportedEfficacy) ? (communityEfficacy + 0) / 2 : (reportedEfficacy + communityEfficacy) / 2;
  const calculatedValue = efficacy * generalSubtotal;
  return parseFloat(calculatedValue.toFixed(3));
};

const calculatePainValue = (data: PainConsultationData): number => {
  const painScore = Number(data['how-severe-pain']) ?? 0;
  const painScale: { [key: number]: number } = {
    0: 0,
    1: 1,
    2: 2,
    3: 7,
    4: 11,
    5: 18,
    6: 23,
    7: 37,
    8: 67,
    9: 88,
    10: 99
  };

  return painScale[painScore] || 0;
};

const getPainReliefRecommendation = (
  key: string,
  painReliefValues: Record<string, PainReliefValue>,
  painReliefTried: StringIndexedObject,
  importance: string[]
): number => {
  // If the treatment was tried and rated 0, return 0
  if (key in painReliefTried && Number(painReliefTried[key]) === 0) {
    return 0;
  }

  const painRefiefValueItem = painReliefValues[key];
  // For tried treatments use their value, for untried use 1
  const reportedEfficacy = key in painReliefTried ? Number(painReliefTried[key]) : 1;
  const efficacySubtotal = averageProduct(reportedEfficacy, painRefiefValueItem.communityEfficacy, painRefiefValueItem.generalSubtotal);
  return calculateRecommendationGrandTotal(key, efficacySubtotal, importance);
};

const generatePainReliefRecommendations = (data: PainConsultationData, country: CountryCode, prescriptions: PrescriptionRecommendations) => {
  const importance = data['important-things-pain-relief'] as string[];
  const painReliefTried = data['forms-of-pain-relief-tried'] as StringIndexedObject;
  const painReliefValues: Record<string, PainReliefValue> = {
    // 'otc-painkillers': {
    //   communityEfficacy: 1.93,
    //   generalSubtotal: 1
    // },
    'cbd-tampons': {
      communityEfficacy: 2.04,
      generalSubtotal: 3
    },
    'heat-therapy': {
      communityEfficacy: 1.95,
      generalSubtotal: 3
    },
    'tens-machines': {
      communityEfficacy: 1.61,
      generalSubtotal: 3
    },
    massages: {
      communityEfficacy: 1.64,
      generalSubtotal: 2
    },
    // 'pelvic-floor-stretches': {
    //   communityEfficacy: 1.43,
    //   generalSubtotal: 3
    // },
    acupuncture: {
      communityEfficacy: 1.48,
      generalSubtotal: 2
    },
    'birth-control-pills': {
      communityEfficacy: 1.52,
      generalSubtotal: 1
    },
    'hormonal-coil-IUS': {
      communityEfficacy: 1.33,
      generalSubtotal: 2
    }
  };

  const hideCBdTampons = country === CountryCode.US;
  const includeCbdTampons = !hideCBdTampons && prescriptions.cbdTampons.eligible && prescriptions.cbdTampons.recommended;

  const recommendations: StringIndexedNumberObject = Object.fromEntries(
    Object.keys(painReliefValues)
      .filter((key) => includeCbdTampons || key !== 'cbd-tampons')
      .map((key) => [key, getPainReliefRecommendation(key, painReliefValues, painReliefTried, importance)])
  );

  return recommendations;
};

const calculateImportanceScore = (importanceFactors: string[], importance: string[]): number =>
  importanceFactors.reduce((score, factor) => score + (importance.includes(factor) ? 1 : 0), 0);

const calculateRecommendationGrandTotal = (key: string, efficacySubtotal: number, importance: string[]): number => {
  const importanceFactorsMap: Record<string, string[]> = {
    'otc-painkillers': ['convenience'],
    'cbd-tampons': ['minimising-side-effects', 'convenience', 'natural-pain-relief'],
    'heat-therapy': ['minimising-side-effects', 'natural-pain-relief', 'cost'],
    'tens-machines': ['minimising-side-effects', 'convenience', 'natural-pain-relief'],
    massages: ['minimising-side-effects', 'natural-pain-relief'],
    'pelvic-floor-stretches': ['minimising-side-effects', 'natural-pain-relief', 'cost'],
    acupuncture: ['minimising-side-effects', 'natural-pain-relief'],
    'birth-control-pills': ['cost'],
    'hormonal-coil-IUS': ['convenience', 'cost']
  };

  const importanceFactors = importanceFactorsMap[key] || [];
  const score = calculateImportanceScore(importanceFactors, importance);
  return efficacySubtotal * score;
};

const generateOtherSymptomResourceRecommendations = (data: PainConsultationData) => {
  const symptomRecommendationMap: Record<string, string[]> = {
    'pain-during-sex': ['penis-rings'],
    acne: ['hormonal-acne-care'],
    'excessive-facial-or-body-hair': ['laser-hair-removal']
  };

  const symptomsDuringSex = (data['experience-symptoms-cycle'] as string[]) || [];

  const recommendations: string[] = symptomsDuringSex.reduce<string[]>((accumulator, symptom) => {
    const recommendation = symptomRecommendationMap[symptom];
    if (recommendation) {
      return accumulator.concat(recommendation);
    }
    return accumulator;
  }, []);

  return recommendations;
};

const generatePainkillerRiskData = (data: PainConsultationData): PainkillerRiskData => {
  const painReliefTried = data['forms-of-pain-relief-tried'] as StringIndexedObject;
  const otcPainkiller = data['otc-painkiller-usually-taken'];
  const prescriptionPainkiller = data['prescription-painkiller-usually-taken'];
  const lowRisk = 8;
  const mediumRisk = 50;
  const highRisk = 92;
  let riskValue = 0;

  const riskFactors = {
    low: ['<1200', '<3600', '<4000'],
    medium: ['<4hours'],
    high: ['>1200', '>3600', '>4000', 'yes']
  };

  if (painReliefTried['otc-painkillers'] === '1' && painReliefTried['prescription-painkillers'] === '1') {
    riskValue = lowRisk;
  }

  const otcPainkillers = ['ibuprofen', 'aspirin', 'paracetamol', 'other'];
  const prescriptionPainkillers = ['naproxen', 'mefenamic-acid', 'dihydrocodeine', 'hydrocodone', 'other'];

  otcPainkillers.forEach((painkiller) => {
    const doseKey = painkiller === 'other' ? 'other-more-than-recommended-24-hours' : `how-many-mg-${painkiller}-24-hours`;
    const dose = data[doseKey] as string;
    const frequencyKey = `how-often-take-${painkiller}`;
    const frequency = data[frequencyKey] as string;

    if (otcPainkiller === painkiller) {
      if (riskFactors.low.includes(dose)) {
        riskValue = lowRisk;
      } else if (riskFactors.high.includes(dose)) {
        riskValue = highRisk;
      } else if (riskFactors.medium.includes(frequency)) {
        riskValue = mediumRisk;
      }
    }
  });

  prescriptionPainkillers.forEach((painkiller) => {
    const doseKey = `${painkiller}-more-often-than-prescribed`;
    const dose = data[doseKey];

    if (prescriptionPainkiller === painkiller) {
      if (dose === 'no') {
        riskValue = mediumRisk;
      } else {
        riskValue = highRisk;
      }
    }
  });

  const otcPainkillerFrequency = otcPainkiller === 'other' ? 'More than recommended' : (data[`how-often-take-${otcPainkiller}`] as string);
  const otcPainkillerDoseExceededKey = otcPainkiller === 'other' ? 'other-more-than-recommended-24-hours' : `how-many-mg-${otcPainkiller}-24-hours`;
  const otcPainkillerDoseExceeded = (data[otcPainkillerDoseExceededKey] as string)?.includes('>') ? 'yes' : 'no';

  let prescriptionPainkillerDoseExceeded = '';
  if (prescriptionPainkiller === 'other') {
    prescriptionPainkillerDoseExceeded = data['other-prescription-painkiller-more-often-than-prescribed'] as string;
  } else {
    prescriptionPainkillerDoseExceeded = (data[otcPainkillerDoseExceededKey] as string)?.includes('>') ? 'yes' : 'no';
  }

  const adverseAffects = (data['adverse-effects-from-pain-relief'] as string[]) ?? [];
  const otcPainkillerName =
    otcPainkiller === 'other' ? (data['otc-painkiller-usually-taken-other'] as string) : formatString(otcPainkiller as string);
  const prescriptionPainkillerName =
    prescriptionPainkiller === 'other'
      ? (data['prescription-painkiller-usually-taken-other'] as string)
      : formatString(prescriptionPainkiller as string);

  const painkillerContext: PainkillerContext = {
    otcPainkiller: otcPainkillerName,
    prescriptionPainkiller: prescriptionPainkillerName,
    otcPainkillerDose: (data[otcPainkillerDoseExceededKey] as string) || '',
    otcPainkillerDoseExceeded,
    otcPainkillerFrequency,
    prescriptionPainkillerDoseExceeded,
    adverseAffects
  };

  return {
    context: painkillerContext,
    riskValue,
    riskContent: generatePainkillerRiskDataForReport(painkillerContext),
    safetyContent: generatePainkillerSafetyDataForReport(painkillerContext)
  };
};

export const generatePainkillerRiskDataForReport = (painkillerContext: PainkillerContext): IconTextObject[] => {
  const array: IconTextObject[] = [];

  if (painkillerContext.prescriptionPainkiller && painkillerContext.prescriptionPainkillerDoseExceeded === 'yes') {
    array.push({
      icon: <ExclamationPurpleIcon />,
      text: `You take ${painkillerContext.prescriptionPainkiller} more often than you've been prescribed. Some prescription painkillers could be addictive if taken for prolonged time and at higher doses`
    });
  } else if (painkillerContext.prescriptionPainkiller) {
    array.push({
      icon: <GreenTickIcon />,
      text: `You take ${painkillerContext.prescriptionPainkiller} within the prescribed dose`,
      backgroundColor: Colors.mint50,
      borderColor: Colors.mint
    });
  }

  if (painkillerContext.otcPainkiller) {
    const dose = parseNumberFromString(painkillerContext.otcPainkillerDose);
    const doseWithinLimit = painkillerContext.otcPainkillerDose.includes('<');
    const frequencyWithinLimit = !painkillerContext.otcPainkillerFrequency.includes('<');

    if (doseWithinLimit && frequencyWithinLimit) {
      array.push({
        icon: <GreenTickIcon />,
        text: `You use ${painkillerContext.otcPainkiller} within the recommended dose and frequency.`,
        backgroundColor: Colors.mint50,
        borderColor: Colors.mint
      });
    } else if (!doseWithinLimit && frequencyWithinLimit) {
      if (dose !== null) {
        array.push({
          icon: <ExclamationYellowIcon />,
          text: `You take ${painkillerContext.otcPainkiller} within the recommended frequency, but exceed the maximum daily dose. You should take less than ${dose}mg per day, while allowing at least a 4-hour interval between each pill intake.`
        });
      } else {
        array.push({
          icon: <ExclamationYellowIcon />,
          text: `You take ${painkillerContext.otcPainkiller} more frequently than recommended.`
        });
      }
    } else if (doseWithinLimit && !frequencyWithinLimit) {
      array.push({
        icon: <ExclamationYellowIcon />,
        text: `You use ${painkillerContext.otcPainkiller} within the recommended dose, but take it too frequently.`
      });
    } else if (dose !== null) {
      array.push({
        icon: <ExclamationYellowIcon />,
        text: `You exceed the recommended daily dose and frequency of taking ${painkillerContext.otcPainkiller}. You should take less than ${dose}mg per day, while allowing at least a 4-hour interval between each pill intake.`
      });
    } else {
      array.push({
        icon: <ExclamationYellowIcon />,
        text: `You exceed the recommended frequency of taking ${painkillerContext.otcPainkiller}.`
      });
    }
  }

  if (painkillerContext.prescriptionPainkiller && painkillerContext.otcPainkiller) {
    array.push({
      icon: <ExclamationYellowIcon />,
      text: `Always consult with your doctor before using over-the-counter painkillers in combination with your prescribed medications`
    });
  }

  const arrayWithBackground = array.map((item) => ({
    ...item,
    backgroundColor: item.backgroundColor || Colors.paleGold25,
    borderColor: item.borderColor || Colors.paleGold50
  }));

  return arrayWithBackground;
};

export const generatePainkillerSafetyDataForReport = (painkillerContext: PainkillerContext): IconTextObject[] => {
  const array: IconTextObject[] = [];

  if (painkillerContext.otcPainkiller) {
    let timescaleText = `Consult your doctor if you need to take ${painkillerContext.otcPainkiller} for several consecutive days`;

    if (painkillerContext.otcPainkiller.toLowerCase() === 'ibuprofen') {
      timescaleText = `Consult your doctor if you need to take ${painkillerContext.otcPainkiller} for more than 10 consecutive days`;
    }

    if (painkillerContext.otcPainkiller.toLowerCase() === 'paracetamol') {
      timescaleText = `Consult your doctor if you need to take ${painkillerContext.otcPainkiller} for more than 3 consecutive days`;
    }

    array.push({
      icon: <ExclamationYellowIcon />,
      text: 'Avoid taking painkillers less than 4 hours apart'
    });

    array.push({
      icon: <ExclamationYellowIcon />,
      text: timescaleText
    });
  }

  if (!painkillerContext.adverseAffects.includes('none')) {
    array.push({
      icon: <ExclamationYellowIcon />,
      text: "You should speak to a doctor about the adverse effects you're experiencing. This may indicate that you need to use a different pain-relief method or lower your dose."
    });
  }

  if (painkillerContext.prescriptionPainkiller && painkillerContext.prescriptionPainkillerDoseExceeded === 'no') {
    array.push({
      icon: <ExclamationYellowIcon />,
      text: 'Prescription painkillers could be addictive if taken for prolonged time and at higher doses'
    });
  }

  if (painkillerContext.prescriptionPainkiller || painkillerContext.otcPainkiller) {
    array.push({
      icon: <PlusIcon />,
      text: 'Supplement or replace painkillers entirely with other pain-relief methods',
      backgroundColor: Colors.mint50,
      borderColor: Colors.mint
    });
  }

  if (!painkillerContext.prescriptionPainkiller && !painkillerContext.otcPainkiller) {
    array.push({
      icon: <StarAltIcon />,
      text: 'NSAIDs (like ibuprofen) are usually considered the safest painkillers for period pain as they can reduce pain and inflammation without the risk of addiction and a low side effects profile, if taken within the recommended doses.',
      backgroundColor: Colors.mint50,
      borderColor: Colors.mint
    });

    array.push({
      icon: <GreenTickIcon />,
      text: 'Keep your risk of long-term health effects low by sticking to painkiller alternatives',
      link: '/dashboard/period-pain-report/?reportTab=deals-and-resources',
      backgroundColor: Colors.mint50,
      borderColor: Colors.mint
    });
  }

  const arrayWithBackground = array.map((item) => ({
    ...item,
    backgroundColor: item.backgroundColor || Colors.paleGold25,
    borderColor: item.borderColor || Colors.paleGold50
  }));

  return arrayWithBackground;
};
